import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ProjectMedia = ({
  formData,
  onFieldChange,
  errors = {}
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getFieldValue = (fieldName) => {
    return formData?.[fieldName] || '';
  };

  const handleFieldChange = (fieldName, value) => {
    onFieldChange(fieldName, value);
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFileUpload(e?.target?.files?.[0]);
    }
  };

  const handleFileUpload = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('نوع الملف غير مدعوم. يرجى اختيار صورة بصيغة JPG, PNG, WebP أو GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file?.size > 5 * 1024 * 1024) {
      alert('حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      // Simulate upload completion
      setTimeout(() => {
        setUploadProgress(100);
        handleFieldChange('featuredImage', previewUrl);
        handleFieldChange('featuredImageAlt', `صورة مميزة للمشروع - ${file?.name}`);

        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgress(0);
      alert('حدث خطأ أثناء رفع الملف');
    }
  };

  const removeImage = () => {
    handleFieldChange('featuredImage', '');
    handleFieldChange('featuredImageAlt', '');
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };



  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Image" size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            الصور والوسائط
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            أضف صورة مميزة ووسائط أخرى للمشروع
          </p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Featured Image Upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            الصورة المميزة
          </label>
          
          {!getFieldValue('featuredImage') ?
          <div
            className={`
                relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
                ${dragActive ?
            'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/50'}
              `
            }
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}>

              <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />

              
              <div className="space-y-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                  <Icon name="Upload" size={24} className="text-muted-foreground" />
                </div>
                
                <div>
                  <p className="text-lg font-medium text-foreground">
                    اسحب وأفلت الصورة هنا
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    أو انقر لاختيار ملف من جهازك
                  </p>
                </div>
                
                <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
                  <span>JPG, PNG, WebP, GIF</span>
                  <span>•</span>
                  <span>حتى 5 ميجابايت</span>
                  <span>•</span>
                  <span>الحد الأدنى 800×600</span>
                </div>
              </div>

              {isUploading &&
            <div className="absolute inset-0 bg-background/80 rounded-lg flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm font-medium text-foreground">
                      جاري الرفع... {uploadProgress}%
                    </p>
                    <div className="w-32 bg-muted rounded-full h-2 mx-auto">
                      <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }} />

                    </div>
                  </div>
                </div>
            }
            </div> :

          <div className="relative group">
              <div className="relative overflow-hidden rounded-lg border border-border">
                <Image
                src={getFieldValue('featuredImage')}
                alt={getFieldValue('featuredImageAlt')}
                className="w-full h-64 object-cover" />

                
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-3">
                  <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef?.current?.click()}
                  iconName="Edit"
                  iconPosition="left"
                  iconSize={16}>

                    تغيير
                  </Button>
                  
                  <Button
                  variant="destructive"
                  size="sm"
                  onClick={removeImage}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={16}>

                    حذف
                  </Button>
                </div>
              </div>
              
              <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden" />

            </div>
          }

          {errors?.featuredImage &&
          <p className="text-sm text-error mt-2 flex items-center space-x-2">
              <Icon name="AlertCircle" size={14} />
              <span>{errors?.featuredImage}</span>
            </p>
          }
        </div>

        {/* Media Settings */}
        <div className="bg-muted/50 rounded-lg p-4 border border-border">
          <h4 className="text-sm font-medium text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Settings" size={16} />
            <span>إعدادات الوسائط</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">ضغط الصور</span>
              <span className="text-foreground font-medium">تلقائي</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">جودة الصورة</span>
              <span className="text-foreground font-medium">عالية</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">تحسين الويب</span>
              <span className="text-success font-medium">مفعل</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">النسخ الاحتياطي</span>
              <span className="text-success font-medium">مفعل</span>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default ProjectMedia;